import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import Api from 'utils/api'

const queryClientOptions = {
  defaultOptions: {
    mutations: {
      retry: false,
    },
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
}

const queryClient = new QueryClient(queryClientOptions)

const isWantedQueryKeyPart = part => part !== null && part !== undefined && part !== ''

const parseEndpointQueryString = queryString => {
  if (!queryString) {
    return undefined
  }

  return Object.fromEntries(new URLSearchParams(queryString))
}

export const generateQueryKey = ({ endpoint, queryKey, queryKeySuffix } = {}) => {
  if (queryKey) {
    return queryKey
  }

  const [endpointPath, queryString] = endpoint.replace(/^\//, '').split('?')
  const endpointParts = endpointPath.split('/').filter(isWantedQueryKeyPart)
  const queryStringData = parseEndpointQueryString(queryString)

  return [
    ...endpointParts,
    ...(queryKeySuffix || []),
    queryStringData,
  ].filter(isWantedQueryKeyPart)
}

const getErrorMessage = error => error?.message || ''

const resultAdapter = ({ defaultValue, result, helpers = {} }) => ({
  ...result,
  ...helpers,
  data: result.data ?? defaultValue,
  errorMessage: getErrorMessage(result.error),
})

export const invalidateQueries = (queryKey, options) =>
  queryClient.invalidateQueries({ queryKey, ...options })

export const removeQueries = (queryKey, options) =>
  queryClient.removeQueries({ queryKey, ...options })

export const setQueryData = (queryKey, data) =>
  queryClient.setQueryData(queryKey, data)

export const useSafeQuery = (queryKey, queryFn, { defaultValue = {}, ...options } = {}) => {
  const currentQueryClient = useQueryClient()
  const queryResult = useQuery({
    queryFn,
    queryKey,
    ...options,
  })

  const helpers = {
    invalidateQuery: invalidateOptions =>
      currentQueryClient.invalidateQueries({ queryKey, ...invalidateOptions }),
    queryKey,
    removeQueries: removeOptions =>
      currentQueryClient.removeQueries({ queryKey, ...removeOptions }),
    setData: data => currentQueryClient.setQueryData(queryKey, data),
  }

  return resultAdapter({
    defaultValue,
    helpers,
    result: queryResult,
  })
}

export const useApiGetQuery = ({
  defaultValue = {},
  endpoint,
  options = {},
  queryKey,
  queryKeySuffix,
  requestOptions,
}) => {
  const generatedQueryKey = generateQueryKey({ endpoint, queryKey, queryKeySuffix })

  return useSafeQuery(
    generatedQueryKey,
    () => Api.get(endpoint, requestOptions),
    {
      defaultValue,
      ...options,
    },
  )
}

const getMutationPayload = variables => {
  if (!variables || typeof variables !== 'object') {
    return variables
  }

  if ('payload' in variables) {
    return variables.payload
  }

  const payload = { ...variables }
  delete payload.endpointParams
  delete payload.requestOptions

  return payload
}

const getMutationEndpoint = (endpoint, variables) => {
  if (typeof endpoint === 'function') {
    return endpoint(variables?.endpointParams || variables)
  }

  return endpoint
}

export const useApiMutation = (endpoint, method = 'post', options = {}) => {
  const mutationResult = useMutation({
    mutationFn: variables => {
      const mutationEndpoint = getMutationEndpoint(endpoint, variables)
      const payload = getMutationPayload(variables)
      const requestOptions = variables?.requestOptions

      if (method === 'delete') {
        return Api.delete(mutationEndpoint, requestOptions)
      }

      return Api[method](mutationEndpoint, payload, requestOptions)
    },
    ...options,
  })

  return resultAdapter({
    defaultValue: {},
    result: mutationResult,
  })
}

export {
  queryClientOptions,
}

export default queryClient
